import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Copy, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { BRAND } from '@/config/brand';
import { formatAddress } from '@/components/layout/LegalPageLayout';
import { useAuth } from '@/store/useAuth';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function copyText(text) {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  const el = document.createElement('textarea');
  el.value = text;
  el.setAttribute('readonly', '');
  el.style.position = 'absolute';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  el.select();
  try {
    document.execCommand('copy');
    return Promise.resolve();
  } catch (err) {
    return Promise.reject(err);
  } finally {
    document.body.removeChild(el);
  }
}

export default function DeleteAccount() {
  const user = useAuth((s) => s.user);
  const isAuthenticated = useAuth((s) => s.isAuthenticated);

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [scope, setScope] = useState('full');
  const [partialDetails, setPartialDetails] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.title = `Delete your account | ${BRAND.name}`;
    return () => {
      document.title = BRAND.name;
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.email) {
      setEmail((prev) => prev || user.email);
      if (user.name) setFullName((prev) => prev || user.name);
    }
  }, [isAuthenticated, user]);

  const validate = () => {
    const next = {};
    const trimmedEmail = email.trim();
    if (!trimmedEmail) next.email = 'Email address is required.';
    else if (!EMAIL_RE.test(trimmedEmail)) next.email = 'Enter a valid email address.';
    if (scope === 'partial' && !partialDetails.trim()) {
      next.partialDetails = 'Please describe which data should be deleted.';
    }
    if (!confirmed) next.confirmed = 'You must confirm that you understand this is permanent.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const isPartial = scope === 'partial';
    const subject = isPartial
      ? `Data deletion request – ${BRAND.name}`
      : `Account deletion request – ${BRAND.name}`;

    const lines = [
      `Request type: ${isPartial ? 'Partial data deletion (keep account)' : 'Full account and all associated data'}`,
      `Email on account: ${email.trim()}`,
      `Full name: ${fullName.trim() || '(not provided)'}`,
    ];
    if (isPartial) {
      lines.push(`Data to delete: ${partialDetails.trim()}`);
    }
    lines.push(
      `Reason: ${reason.trim() || '(not provided)'}`,
      `Confirmed permanence: yes`,
      `Request timestamp (ISO): ${new Date().toISOString()}`,
      'Sent from casahom.com/delete-account',
    );

    const mailto = `mailto:${BRAND.support.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join('\n'))}`;
    window.location.href = mailto;
    setSubmitted(true);
  };

  const handleCopyEmail = async () => {
    try {
      await copyText(BRAND.support.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div>
      <section className="relative hero-gradient noise-overlay">
        <div className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6 sm:py-20 lg:px-8">
          <p className="font-display text-sm font-semibold uppercase tracking-[0.2em] text-white/80">
            {BRAND.legalName}
          </p>
          <h1 className="font-display mx-auto mt-4 max-w-3xl text-3xl font-bold text-white sm:text-4xl">
            Delete your {BRAND.name} account
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-white/85">
            This page explains how to request deletion of your account and associated data for the {BRAND.name} app
            (Android &amp; iOS) and {BRAND.name}.com, operated by {BRAND.legalName}.
          </p>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
        <div className="space-y-10 text-slate-600 leading-relaxed [&_h2]:font-display [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-slate-900 [&_h2]:sm:text-2xl [&_ol]:mt-3 [&_ol]:list-decimal [&_ol]:space-y-2 [&_ol]:pl-5 [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5 [&_p]:mt-3 [&_a]:text-blue-700 [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-blue-800">
          <section>
            <h2>Option 1 — Delete in the app</h2>
            <p>
              Apple requires that account deletion can be started from within the app. Use these steps on mobile or on
              the website:
            </p>
            <ol>
              <li>Open the {BRAND.name} app and sign in to your account.</li>
              <li>Go to Profile / Account (Dashboard → Account settings).</li>
              <li>Choose &ldquo;Delete account&rdquo; and confirm.</li>
            </ol>
            <p>
              On the website: sign in at{' '}
              <a href="https://casahom.com">casahom.com</a> → Dashboard → Account settings → Delete account.
            </p>
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              Uninstalling or deleting the app does <strong>not</strong> delete your account or your data.
            </p>
          </section>

          <section>
            <h2>Option 2 — Request deletion from this page</h2>
            <p>
              You can request account or data deletion from the web without reinstalling the app. Submit the form below;
              it opens your email app with a prefilled message to {BRAND.support.email}.
            </p>

            {submitted ? (
              <Alert className="mt-6 border-emerald-200 bg-emerald-50 text-emerald-950">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden />
                <AlertTitle>Request ready to send</AlertTitle>
                <AlertDescription className="mt-2 space-y-3 text-emerald-900">
                  <p>
                    Your email app should open with the request. If it didn&apos;t, email us at{' '}
                    <a href={`mailto:${BRAND.support.email}`} className="font-medium underline">
                      {BRAND.support.email}
                    </a>{' '}
                    from the address on your account with subject &ldquo;Account deletion request&rdquo;.
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <code className="rounded bg-white/80 px-2 py-1 text-sm text-slate-800">
                      {BRAND.support.email}
                    </code>
                    <Button type="button" variant="outline" size="sm" onClick={handleCopyEmail} className="gap-1.5">
                      <Copy className="h-3.5 w-3.5" aria-hidden />
                      {copied ? 'Copied' : 'Copy email'}
                    </Button>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="px-0 text-emerald-800 underline"
                    onClick={() => setSubmitted(false)}
                  >
                    Submit another request
                  </Button>
                </AlertDescription>
              </Alert>
            ) : (
              <Card className="mt-6 border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-display text-lg text-slate-900">Deletion request form</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} noValidate className="space-y-5">
                    <div>
                      <Label htmlFor="da-email">Email address on the account</Label>
                      <Input
                        id="da-email"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-1.5 rounded-xl"
                        aria-invalid={errors.email ? 'true' : undefined}
                        aria-describedby={errors.email ? 'da-email-error' : undefined}
                        required
                      />
                      {errors.email ? (
                        <p id="da-email-error" className="mt-1.5 text-sm text-red-600" role="alert">
                          {errors.email}
                        </p>
                      ) : null}
                    </div>

                    <div>
                      <Label htmlFor="da-name">Full name (optional)</Label>
                      <Input
                        id="da-name"
                        type="text"
                        autoComplete="name"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="mt-1.5 rounded-xl"
                      />
                    </div>

                    <fieldset>
                      <legend className="text-sm font-medium text-slate-900">What should we delete?</legend>
                      <RadioGroup
                        value={scope}
                        onValueChange={setScope}
                        className="mt-3 space-y-3"
                        aria-label="Deletion scope"
                      >
                        <div className="flex items-start gap-3">
                          <RadioGroupItem value="full" id="da-scope-full" className="mt-0.5" />
                          <Label htmlFor="da-scope-full" className="font-normal leading-snug">
                            Delete my entire account and all associated data
                          </Label>
                        </div>
                        <div className="flex items-start gap-3">
                          <RadioGroupItem value="partial" id="da-scope-partial" className="mt-0.5" />
                          <Label htmlFor="da-scope-partial" className="font-normal leading-snug">
                            Delete only certain data (keep my account)
                          </Label>
                        </div>
                      </RadioGroup>
                    </fieldset>

                    {scope === 'partial' ? (
                      <div>
                        <Label htmlFor="da-partial">Which data should we delete?</Label>
                        <Textarea
                          id="da-partial"
                          rows={3}
                          value={partialDetails}
                          onChange={(e) => setPartialDetails(e.target.value)}
                          className="mt-1.5 rounded-xl"
                          aria-invalid={errors.partialDetails ? 'true' : undefined}
                          aria-describedby={errors.partialDetails ? 'da-partial-error' : undefined}
                          required
                        />
                        {errors.partialDetails ? (
                          <p id="da-partial-error" className="mt-1.5 text-sm text-red-600" role="alert">
                            {errors.partialDetails}
                          </p>
                        ) : null}
                      </div>
                    ) : null}

                    <div>
                      <Label htmlFor="da-reason">Reason (optional)</Label>
                      <Textarea
                        id="da-reason"
                        rows={3}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        className="mt-1.5 rounded-xl"
                        placeholder="Tell us anything that helps us process your request"
                      />
                    </div>

                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="da-confirm"
                        checked={confirmed}
                        onCheckedChange={(v) => setConfirmed(v === true)}
                        aria-invalid={errors.confirmed ? 'true' : undefined}
                        aria-describedby={errors.confirmed ? 'da-confirm-error' : undefined}
                      />
                      <Label htmlFor="da-confirm" className="font-normal leading-snug">
                        I understand this is permanent and cannot be undone.
                      </Label>
                    </div>
                    {errors.confirmed ? (
                      <p id="da-confirm-error" className="-mt-3 text-sm text-red-600" role="alert">
                        {errors.confirmed}
                      </p>
                    ) : null}

                    <Button type="submit" className="w-full rounded-xl sm:w-auto">
                      <Mail className="mr-2 h-4 w-4" aria-hidden />
                      Send deletion request
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </section>

          <section>
            <h2>Identity verification</h2>
            <p>
              To protect your account, we only process deletion requests sent from the email address on the account, or
              we may ask you to confirm by replying to a verification email. We never ask for your password.
            </p>
          </section>

          <section>
            <h2>Timeline</h2>
            <p>
              We confirm receipt within <strong>3 business days</strong> and complete deletion within{' '}
              <strong>30 days</strong> of verification. You will receive a confirmation email when the process is done.
            </p>
          </section>

          <section>
            <h2>What data is deleted</h2>
            <p>When you request full account deletion, we delete or irreversibly anonymize:</p>
            <ul>
              <li>Account profile (name, email, phone, photo)</li>
              <li>Login credentials</li>
              <li>Saved homes / favorites</li>
              <li>Saved searches and alerts</li>
              <li>Notifications</li>
              <li>Messages and inquiries to agents</li>
              <li>Listings you created</li>
              <li>Preferences and device / push tokens</li>
              <li>Usage data linked to your account</li>
            </ul>
          </section>

          <section>
            <h2>What we may keep and for how long</h2>
            <ul>
              <li>
                Transaction, payment, and invoice records (processed by our payment provider, e.g. Stripe) may be kept
                up to <strong>7 years</strong> for tax and accounting law.
              </li>
              <li>
                Records needed for fraud prevention, security, or legal disputes are kept as long as legally required.
              </li>
              <li>Anonymized or aggregated analytics that can no longer identify you may be retained.</li>
              <li>Backups are purged automatically within <strong>90 days</strong>.</li>
            </ul>
            <p>
              <strong>Active paid subscriptions:</strong> cancel them before or after deletion. Deleting your account
              does not automatically cancel store-billed subscriptions.
            </p>
            <ul>
              <li>
                App Store: iPhone Settings → Apple ID → Subscriptions
              </li>
              <li>
                Google Play: Play Store → Payments &amp; subscriptions → Subscriptions
              </li>
            </ul>
          </section>

          <section>
            <h2>Also useful</h2>
            <p>
              See our <Link to="/privacy">Privacy Policy</Link> for how we handle personal information, or{' '}
              <Link to="/contact">contact us</Link> with other questions. US privacy rights (including for Florida and
              California residents) can be exercised the same way — email us from the address on your account or use
              the form above.
            </p>
          </section>

          <section>
            <h2>Contact</h2>
            <aside className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
              <p className="font-semibold text-slate-900">{BRAND.legalName}</p>
              <p className="mt-2">{formatAddress()}</p>
              <p className="mt-2">
                <a href={`mailto:${BRAND.support.email}`}>{BRAND.support.email}</a>
              </p>
              {BRAND.support.phone ? (
                <p className="mt-1">
                  <a href={`tel:${BRAND.support.phone.replace(/\D/g, '')}`}>{BRAND.support.phone}</a>
                </p>
              ) : null}
            </aside>
          </section>

          <p className="text-sm text-slate-500">Last updated: September 29, 2026</p>
        </div>
      </article>
    </div>
  );
}
